import React, { useState, useEffect } from 'react';
import axios from '../services/axiosConfig';
import '../styles/asistencia.css';

function ControlAsistencia() {
  const [pacientes, setPacientes] = useState([]);
  const [asistenciaHoy, setAsistenciaHoy] = useState({});
  const [buscando, setBuscando] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [historialVisible, setHistorialVisible] = useState(false);
  const [historialPaciente, setHistorialPaciente] = useState(null);
  const [asistenciasHistorial, setAsistenciasHistorial] = useState([]);

  useEffect(() => {
    cargarPacientes();
    cargarAsistenciaHoy();
    
    // Recargar cada 30 segundos
    const intervalo = setInterval(cargarAsistenciaHoy, 30000);
    return () => clearInterval(intervalo);
  }, []);

  const cargarPacientes = async () => {
    try {
      const response = await axios.get('/pacientes');
      const pacientes = Array.isArray(response.data) ? response.data : response.data.data;
      setPacientes(pacientes || []);
    } catch (error) {
      console.error('Error cargando pacientes:', error);
    }
  };

  const cargarAsistenciaHoy = async () => {
    try {
      const hoy = new Date().toISOString().split('T')[0];
      const response = await axios.get('/api/asistencia/reporte?fecha_inicio=' + hoy + '&fecha_fin=' + hoy);
      
      // Crear mapa de asistencias por paciente
      const mapa = {};
      const asistencias = response.data.data || [];
      asistencias.forEach(a => {
        mapa[a.paciente_id] = a;
      });
      setAsistenciaHoy(mapa);
    } catch (error) {
      console.error('Error cargando asistencias:', error);
      setAsistenciaHoy({});
    }
  };

  const pacientesFiltrados = pacientes.filter(p =>
    p.nombre.toLowerCase().includes(buscando.toLowerCase()) ||
    (p.email && p.email.toLowerCase().includes(buscando.toLowerCase())) ||
    (p.telefono && p.telefono.includes(buscando))
  );

  const handleCheckIn = async (paciente_id) => {
    try {
      setEnviando(true);
      const response = await axios.post('/api/asistencia/check-in', { 
        paciente_id,
        turno_id: null 
      });
      
      setMensaje('✅ Check-in registrado');
      await cargarAsistenciaHoy();
      
      setTimeout(() => setMensaje(''), 3000);
    } catch (error) {
      console.error('Error en check-in:', error);
      setMensaje('❌ Error registrando check-in');
    } finally {
      setEnviando(false);
    }
  };

  const handleCheckOut = async (paciente_id) => {
    try {
      setEnviando(true);
      const response = await axios.post('/api/asistencia/check-out', { paciente_id });
      
      setMensaje('✅ Check-out registrado');
      await cargarAsistenciaHoy();
      
      setTimeout(() => setMensaje(''), 3000);
    } catch (error) {
      console.error('Error en check-out:', error);
      setMensaje('❌ Error registrando check-out');
    } finally {
      setEnviando(false);
    }
  };

  const verHistorial = async (paciente) => {
    try {
      setHistorialPaciente(paciente);
      const response = await axios.get(`/api/asistencia/paciente/${paciente.id}?limit=30`);
      const historiales = response.data.data || [];
      setAsistenciasHistorial(historiales);
      setHistorialVisible(true);
    } catch (error) {
      console.error('Error cargando historial:', error);
      setMensaje('❌ Error cargando historial');
      setTimeout(() => setMensaje(''), 3000);
    }
  };

  return (
    <div className="control-asistencia">
      <div className="asistencia-header">
        <h2>📋 Control de Asistencia</h2>
        <p>Registro de entrada y salida de pacientes</p>
      </div>

      {mensaje && (
        <div className={`mensaje ${mensaje.includes('✅') ? 'exito' : 'error'}`}>
          {mensaje}
        </div>
      )}

      <div className="asistencia-container">
        {!historialVisible && (
          <>
            <div className="busqueda-paciente">
              <input
                type="text"
                placeholder="🔍 Busca paciente por nombre, email o teléfono..."
                value={buscando}
                onChange={(e) => setBuscando(e.target.value)}
                className="busqueda-input"
              />
            </div>

            <div className="pacientes-lista">
              {pacientesFiltrados.length > 0 ? (
                pacientesFiltrados.map(paciente => {
                  const asistencia = asistenciaHoy[paciente.id];
                  const tieneCheckIn = asistencia && asistencia.hora_entrada;
                  const tieneCheckOut = asistencia && asistencia.hora_salida;

                  return (
                    <div key={paciente.id} className="paciente-card">
                      <div className="paciente-info">
                        <h3>👤 {paciente.nombre}</h3>
                        <p>📧 {paciente.email || 'Sin email'}</p>
                        <p>📱 {paciente.telefono || 'Sin teléfono'}</p>

                        {tieneCheckIn && (
                          <div className="asistencia-info">
                            <span className="entrada">
                              ✅ Entrada: {new Date(asistencia.hora_entrada).toLocaleTimeString('es-AR')}
                            </span>
                            {tieneCheckOut && (
                              <span className="salida">
                                🚪 Salida: {new Date(asistencia.hora_salida).toLocaleTimeString('es-AR')}
                              </span>
                            )}
                            {asistencia.duracion_minutos && (
                              <span className="duracion">
                                ⏱️ {asistencia.duracion_minutos} min
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="paciente-acciones">
                        {!tieneCheckIn && (
                          <button
                            className="btn-checkin"
                            onClick={() => handleCheckIn(paciente.id)}
                            disabled={enviando}
                            title="Registrar entrada"
                          >
                            ➡️ Check-in
                          </button>
                        )}

                        {tieneCheckIn && !tieneCheckOut && (
                          <button
                            className="btn-checkout"
                            onClick={() => handleCheckOut(paciente.id)}
                            disabled={enviando}
                            title="Registrar salida"
                          >
                            ⬅️ Check-out
                          </button>
                        )}

                        <button
                          className="btn-historial"
                          onClick={() => verHistorial(paciente)}
                          title="Ver historial de asistencia"
                        >
                          📊 Historial
                        </button>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="empty-state">
                  <p>No hay pacientes que coincidan con la búsqueda</p>
                </div>
              )}
            </div>
          </>
        )}

        {historialVisible && historialPaciente && (
          <div className="historial-view">
            <div className="historial-header">
              <button className="btn-volver" onClick={() => setHistorialVisible(false)}>
                ← Volver
              </button>
              <h3>📊 Historial - {historialPaciente.nombre}</h3>
            </div>

            {asistenciasHistorial.length > 0 ? (
              <table className="historial-tabla">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Entrada</th>
                    <th>Salida</th>
                    <th>Duración</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {asistenciasHistorial.map(a => (
                    <tr key={a.id} className={`estado-${a.estado}`}>
                      <td>{new Date(a.fecha_asistencia).toLocaleDateString('es-AR')}</td>
                      <td>
                        {a.hora_entrada 
                          ? new Date(a.hora_entrada).toLocaleTimeString('es-AR')
                          : '-'
                        }
                      </td>
                      <td>
                        {a.hora_salida
                          ? new Date(a.hora_salida).toLocaleTimeString('es-AR')
                          : '-'
                        }
                      </td>
                      <td>{a.duracion_minutos ? `${a.duracion_minutos} min` : '-'}</td>
                      <td>
                        <span className={`estado-badge estado-${a.estado}`}>
                          {a.estado === 'presente' && '✅ Presente'}
                          {a.estado === 'ausente' && '❌ Ausente'}
                          {a.estado === 'retrasado' && '⏰ Retrasado'}
                          {a.estado === 'cancelado' && '🚫 Cancelado'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="empty-state">
                <p>No hay registros de asistencia</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ControlAsistencia;
